import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { AuthService } from "./../_services";
import { Role } from "./../_models";
import { API, graphqlOperation } from "aws-amplify";
import { createOrder, updateOrder } from "../../graphql/mutations";
import {
  listProducts,
  listOrders,
  listProfiles,
  listInventorys,
} from "../../graphql/queries";
import { BehaviorSubject, Observable } from "rxjs";
import * as AWS from "aws-sdk";
import * as moment from "moment";
import config from "../../aws-exports";

@Injectable({ providedIn: "root" })
export class PortalService {
  products: Array<any>;
  orders: Array<any>;
  private cartCount: BehaviorSubject<number>;

  constructor(private http: HttpClient, private authService: AuthService) {
    if (localStorage.getItem("cart") === null) {
      this.cartCount = new BehaviorSubject<number>(0);
    } else {
      this.cartCount = new BehaviorSubject<number>(
        JSON.parse(localStorage.getItem("cart")).count
      );
    }
  }

  setCartCount(count): void {
    this.cartCount.next(count);
  }

  getCartCount(): Observable<number> {
    return this.cartCount.asObservable();
  }

  async getAllProducts() {
    var response = await API.graphql(graphqlOperation(listProducts));
    const filteredProducts = (response as any).data.listProducts.items;

    if (filteredProducts.length > 1) {
      filteredProducts.sort(function (a, b) {
        return a.displayId - b.displayId;
      });
    }

    return filteredProducts;
  }

  async getProducts(productType) {
    var response = await API.graphql(
      graphqlOperation(listProducts, {
        filter: {
          productType: {
            eq: productType,
          },
        },
      })
    );
    const filteredProducts = (response as any).data.listProducts.items;

    if (filteredProducts.length > 1) {
      filteredProducts.sort(function (a, b) {
        return a.displayId - b.displayId;
      });
    }

    return filteredProducts;
  }

  async getProduct(productId) {
    var response = await API.graphql(
      graphqlOperation(listProducts, {
        filter: {
          id: {
            eq: productId,
          },
        },
      })
    );
    return (response as any).data.listProducts.items[0];
  }

  async cartPlus(product) {
    if (localStorage.getItem("cart") === null) {
      localStorage.setItem(
        "cart",
        JSON.stringify({ items: {}, totalAmount: 0, count: 0 })
      );
    }

    var cartItems = JSON.parse(localStorage.getItem("cart"));

    var newItem;
    if (product.id in cartItems.items) {
      const oldItem = cartItems.items[product.id];
      newItem = {
        quantity: oldItem["quantity"] + 1,
        price: oldItem["price"],
        title: oldItem["title"],
        sum: oldItem["sum"] + product.price,
      };
    } else {
      newItem = {
        quantity: 1,
        price: product.price,
        title: product.title,
        sum: product.price,
      };
    }

    cartItems.items[product.id] = newItem;
    cartItems.totalAmount = cartItems.totalAmount + product.price;
    cartItems.count = cartItems.count + 1;
    localStorage.setItem("cart", JSON.stringify(cartItems));
    this.setCartCount(cartItems.count);
  }

  async cartMinus(product) {
    var cartItems = JSON.parse(localStorage.getItem("cart"));
    const oldItem = cartItems.items[product.id];
    const currentQty = oldItem["quantity"];

    var newItem;
    if (currentQty > 0) {
      newItem = {
        quantity: oldItem["quantity"] - 1,
        price: oldItem["price"],
        title: oldItem["title"],
        sum: oldItem["sum"] - product.price,
      };
      cartItems.items[product.id] = newItem;
      cartItems.totalAmount = cartItems.totalAmount - product.price;
      cartItems.count = cartItems.count - 1;
      localStorage.setItem("cart", JSON.stringify(cartItems));
      this.setCartCount(cartItems.count);
    }
  }

  async getOrders(currentUser) {
    let orders = [];
    let response = null;
    const username = currentUser.email;
    if (currentUser.role === Role.Admin) {
      response = await API.graphql(
        graphqlOperation(listOrders, {
          limit: 50,
          sort: {
            direction: "asc",
            field: "orderId",
          },
        })
      );
    } else {
      response = await API.graphql(
        graphqlOperation(listOrders, {
          limit: 50,
          filter: { username: { eq: username } },
        })
      );
    }

    response = (response as any).data.listOrders.items;

    await Promise.all(
      response.map(async (order) => {
        const user = await this.fetchProfile(order.username);
        order.fullname = user.fullname;
        order.phone = user.phone;
        order.pincode = user.pincode;
        order.address = user.address;
        orders.push(order);
      })
    );

    return orders;
  }

  async fetchProfile(username) {
    let response = await API.graphql(
      graphqlOperation(listProfiles, {
        filter: {
          username: {
            eq: username,
          },
        },
      })
    );
    return (response as any).data.listProfiles.items[0];
  }

  async sendMail(order) {
    AWS.config.update({
      accessKeyId: config.accessKeyId,
      secretAccessKey: config.secretAccessKey,
      region: config.aws_project_region,
    });

    const user = await this.fetchProfile(order.username);
    const fullname = user.fullname;
    const phone = user.phone;

    var items = "<table>";
    order.items.forEach((element) => {
      items +=
        "<tr><td>" +
        element.productTitle +
        "</td><td>" +
        element.quantity +
        "</td></tr>";
    });
    items += "</table>";

    const htmlData = `<html>
      <body>
        Name: ${fullname}
        <br />
        Email: ${order.username}
        <br />
        Phone: ${phone}
        <br />
        Date: ${moment(order.date).format("DD-MM-YYYY")}
        <br />
        Order Id: <b>${order.orderId}</b>
        <br />
        Total Amount: <b>${order.totalAmount.toFixed(2)}</b>
        <br /><br />
        Items: ${items}
        <br />
        Please do not respond to this mail as it is auto-generated by kari.
      </body>
    </html>`;

    const params = {
      Destination: { ToAddresses: [config.toEmail, config.fromEmail] },
      Message: {
        Body: {
          Html: { Charset: "UTF-8", Data: htmlData },
          Text: { Charset: "UTF-8", Data: "" },
        },
        Subject: { Charset: "UTF-8", Data: "New Order has been Placed" },
      },
      Source: config.fromEmail,
    };

    const sendEmail = new AWS.SES().sendEmail(params).promise();

    sendEmail
      .then((data) => {
        console.log("email submitted to SES", data);
      })
      .catch((error) => {
        console.log(error);
      });
  }

  async addOrder(paymentType) {
    const username = this.authService.currentUserValue.email;
    var cartItems = JSON.parse(localStorage.getItem("cart"));

    const status = "pending";
    const date = new Date();
    let expectedAt = new Date(date);
    expectedAt.setDate(expectedAt.getDate() + 3);
    const paidAt = paymentType === "card" ? date.toISOString() : "";

    var items = [];
    for (const key in cartItems.items) {
      items.push({
        productId: key,
        productPrice: cartItems.items[key].price,
        productTitle: cartItems.items[key].title,
        quantity: cartItems.items[key].quantity,
        sum: cartItems.items[key].sum,
      });
    }

    items = items.filter(function (obj) {
      return obj.quantity !== 0;
    });

    const totalAmount = cartItems.totalAmount;

    var orders;
    var orderId = 5000;
    orders = await API.graphql(graphqlOperation(listOrders));

    orders.data.listOrders.items.map((order) => {
      if (parseInt(order.orderId) > orderId) {
        orderId = parseInt(order.orderId);
      }
    });
    orderId = orderId + 1;

    const order = {
      date: date.toISOString(),
      items,
      totalAmount,
      username,
      status,
      paidAt,
      paymentType,
      expectedAt: expectedAt.toISOString(),
      orderId,
    };

    try {
      await API.graphql(graphqlOperation(createOrder, { input: order }));

      this.sendMail(order);

      localStorage.setItem(
        "cart",
        JSON.stringify({ items: {}, totalAmount: 0, count: 0 })
      );
      this.setCartCount(0);
      return;
    } catch (error) {
      throw error;
    }
  }

  async fetchInventories() {
    var response = await API.graphql(graphqlOperation(listInventorys));
    return (response as any).data.listInventorys.items;
  }

  async fetchStock() {
    var response = await API.graphql(graphqlOperation(listProducts));
    return (response as any).data.listProducts.items;
  }

  async fetchSales(start, end) {
    const sales = [];
    let total = 0;
    let allSales = {};

    let filter = {
      or: [
        {
          status: { eq: "delivered" },
        },
        {
          status: { eq: "completed" },
        },
      ],
    };

    const fromDate = new Date(start);
    const toDate = new Date(end);
    let checkDate = new Date();

    const orders = await API.graphql(
      graphqlOperation(listOrders, { filter: filter })
    );

    orders.data.listOrders.items.map((order) => {
      checkDate = new Date(order.deliveredAt);
      if (fromDate < checkDate && checkDate < toDate) {
        order.items.forEach((i) => {
          total += i.sum;
          if (allSales[i.productId]) {
            allSales[i.productId].quantity += i.quantity;
            allSales[i.productId].sum += i.sum;
          } else {
            allSales = {
              ...allSales,
              [i.productId]: {
                title: i.productTitle,
                quantity: i.quantity,
                sum: i.sum,
              },
            };
          }
        });
      }
    });

    Object.entries(allSales).forEach(([k, v]) => {
      sales.push({ title: v["title"], quantity: v["quantity"], sum: v["sum"] });
    });

    return { sales, total };
  }
}
